import { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { FontFamily, MultiPageImageModel, MultiPageVideoModel } from '@bynder-studio/render-core';
import { ImageManipulationRenderer, PlaybackManipulationRenderer } from '@bynder-studio/render-web';
import useAccessRights from 'packages/hooks/useAccessRights';
import { Account, ActionStatus, ColorPaletteAction } from 'packages/hooks/useAccessRights/types';
import { getUser } from 'packages/store/user/user.selectors';
import { colorPalettes } from 'packages/store/brandColors/brandColors.selectors';
import { collectTopLevelElements } from '~/common/editor/timeline/timeline-actions/components/utils.js';
import { fontsElementsSelector } from 'packages/store/creativeEditor/creativeEditor.selectors';
import { exportElementsWithStyles, parseAndApplyElements } from 'packages/common/Components/utils';
import { CreativeType } from 'packages/types/creative';
import { isCtrlKey } from '~/helpers/hotKeys';
import { syncBrandColors } from '../../pages/editor/syncBrandColors';
import { useIsModalOpen } from '~/hooks/useIsModalOpen';

// cusotom mime type is used to avoid pasting element data to places other than our editor
// https://bynder.atlassian.net/browse/VBS-17415
const ELEMENT_BLOB_MIME_TYPE = 'application/vbs-element';
// web preffix is necessary for custom mime types
const ELEMENT_MIME_TYPE = `web ${ELEMENT_BLOB_MIME_TYPE}`;

type Props = {
    creativeModel?: MultiPageImageModel | MultiPageVideoModel;
    manipulationRenderer?: PlaybackManipulationRenderer | ImageManipulationRenderer;
    creativeId: number;
    type: CreativeType;
};

const useCopyPasteShortcuts = ({ creativeModel, manipulationRenderer, creativeId, type }: Props) => {
    const { isAccountActionAllowed } = useAccessRights();
    const fonts: FontFamily[] = useSelector(fontsElementsSelector);
    const user = useSelector(getUser);
    const { palettes } = useSelector(colorPalettes);
    const isModalOpen = useIsModalOpen();

    const copyAction = useCallback(
        (copiedElements) => {
            if (!creativeModel) {
                return;
            }

            const { elements, textStyles, contentProperties } = exportElementsWithStyles(creativeModel, copiedElements);

            const json = JSON.stringify({
                elements,
                textStyles,
                contentProperties,
                currentCompany: user?.currentCompany,
                bynderUserId: user?.user?.bynderUserId,
                identityId: user?.user?.identityId,
                creativeId,
                type,
            });

            const jsonClipboardItem = new ClipboardItem({
                [ELEMENT_MIME_TYPE]: new Blob([json], { type: ELEMENT_BLOB_MIME_TYPE }),
            });

            navigator.clipboard
                .write([jsonClipboardItem])
                .catch((error) => console.error('Failed to copy elements to clipboard:', error));
        },
        [creativeModel, user?.currentCompany, user?.user?.bynderUserId, user?.user?.identityId, isModalOpen],
    );

    const copy = useCallback(
        (e: KeyboardEvent) => {
            if (!manipulationRenderer || isModalOpen) {
                return;
            }

            if (isCtrlKey(e) && (e.code === 'KeyC' || e.keyCode === 67)) {
                // User copy text from input or textarea. Skip it
                if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
                    return;
                }

                // User copy text, not elements. Skip it
                if (manipulationRenderer.canvasManipulation?.compositor?.textEditor) {
                    return;
                }

                const copiedElements = collectTopLevelElements(manipulationRenderer.getSelectedElements());

                // Nothing to copy
                if (copiedElements.length === 0) {
                    return;
                }

                copyAction(copiedElements);
            }
        },
        [copyAction, manipulationRenderer, isModalOpen],
    );

    const paste = useCallback(
        (e: KeyboardEvent) => {
            if (!creativeModel || isModalOpen) {
                return;
            }

            if (isCtrlKey(e) && (e.code === 'KeyV' || e.keyCode === 86)) {
                navigator.clipboard
                    .read()
                    .then((clipboardItems) => {
                        if (!clipboardItems[0].types.includes(ELEMENT_MIME_TYPE)) {
                            return null;
                        }

                        return clipboardItems[0].getType(ELEMENT_MIME_TYPE);
                    })
                    .then((blob) => (blob ? blob.text() : null))
                    .then((text) => {
                        if (!text) {
                            return;
                        }

                        try {
                            const json = JSON.parse(text);

                            if (
                                json.currentCompany !== user?.currentCompany ||
                                json.identityId !== user?.user?.identityId ||
                                json.bynderUserId !== user?.user?.bynderUserId
                            ) {
                                return;
                            }

                            const startFrame =
                                manipulationRenderer instanceof PlaybackManipulationRenderer
                                    ? manipulationRenderer.getCurrentFrame()
                                    : 0;

                            parseAndApplyElements(creativeModel, json, fonts, { startFrame });

                            if (
                                isAccountActionAllowed(
                                    ColorPaletteAction.EDIT_COLOR_PALETTE,
                                    Account.COLOR_PALETTES,
                                ) === ActionStatus.ALLOWED
                            ) {
                                syncBrandColors(creativeModel, palettes, () => null);
                            }
                        } catch (error) {
                            // if not a json data it will throw an error and we will continue execution
                        }
                    })
                    .catch((error) => console.error('Failed to paste elements to clipboard:', error));
            }
        },
        [creativeModel, manipulationRenderer, isModalOpen],
    );

    const cutAction = useCallback(
        (cutElements) => {
            if (!creativeModel) {
                return;
            }

            const editorElements = creativeModel.getAllElementsRecursively();
            const elementsToDelete = editorElements.filter((el) => cutElements.includes(el.id));
            const remainingElements = editorElements.filter((el) => !cutElements.includes(el.id));

            const elementsWithMask = editorElements.filter(
                (el) => 'mask' in el && el.mask !== null && !cutElements.includes(el.id),
            );

            const maskedElementsToUpdate = elementsWithMask.filter((maskedElement) =>
                editorElements.find((el) => el.id === maskedElement.id),
            );

            const detachedMaskIds = elementsToDelete
                .filter((el) => 'mask' in el && el.mask !== null)
                .map((el) => 'mask' in el && el.mask?.elementId);

            const masksToUpdate = remainingElements.filter((el) => detachedMaskIds.includes(el.id));

            creativeModel.removeElements(cutElements);

            if (masksToUpdate) {
                masksToUpdate.forEach((mask) => {
                    creativeModel.updateElement(mask.id, {
                        mask: null,
                    });
                });
            }

            if (maskedElementsToUpdate) {
                maskedElementsToUpdate.forEach((element) => {
                    if ('mask' in element && cutElements.includes(element.mask?.elementId)) {
                        creativeModel.updateElement(element.id, {
                            mask: null,
                        });
                    }
                });
            }
        },
        [creativeModel, isModalOpen],
    );

    const cut = useCallback(
        (e: KeyboardEvent) => {
            if (!manipulationRenderer) {
                return;
            }

            if (isCtrlKey(e) && (e.code === 'KeyX' || e.keyCode === 88)) {
                // User cut text from input or textarea. Skip it
                if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
                    return;
                }

                // User cut text, not elements. Skip it
                if (manipulationRenderer.canvasManipulation?.compositor?.textEditor) {
                    return;
                }

                const cutElements = collectTopLevelElements(manipulationRenderer.getSelectedElements());

                // Nothing to cut
                if (cutElements.length === 0) {
                    return;
                }

                copyAction(cutElements);
                cutAction(cutElements.map((element) => element.id));
            }
        },
        [copyAction, cutAction, manipulationRenderer, isModalOpen],
    );

    useEffect(() => {
        document.addEventListener('keydown', copy);
        document.addEventListener('keydown', paste);
        document.addEventListener('keydown', cut);

        return () => {
            document.removeEventListener('keydown', copy);
            document.removeEventListener('keydown', paste);
            document.removeEventListener('keydown', cut);
        };
    }, [copy, paste, cut]);
};

export default useCopyPasteShortcuts;
